import React, { useEffect, useMemo, useState } from "react";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  addMonths,
  format as formatDateFns,
  getMonth,
  getYear,
} from "date-fns";
import { de, enUS } from "date-fns/locale";

import useToast from "../../components/useToast";
import DownloadBuilder from "../../components/DownloadBuilder";
import { API_BASE_URL } from "../../config";
import { downloadFilenameFromDisposition } from "../../utils/downloadFilename";
import "../../i18n";
import AppButton from "../../design-system/components/AppButton";
import AppCheckbox from "../../design-system/components/AppCheckbox";
import AppChip from "../../design-system/components/AppChip";
import AppConfirmDialog from "../../design-system/components/AppConfirmDialog";
import AppDialog from "../../design-system/components/AppDialog";
import AppDialogBody from "../../design-system/components/AppDialogBody";
import AppDialogFooter from "../../design-system/components/AppDialogFooter";
import AppDialogHeader from "../../design-system/components/AppDialogHeader";
import AppDivider from "../../design-system/components/AppDivider";
import AppField from "../../design-system/components/AppField";
import AppFieldGrid from "../../design-system/components/AppFieldGrid";
import AppFieldStack from "../../design-system/components/AppFieldStack";
import AppInput from "../../design-system/components/AppInput";
import AppPeriodNavigator from "../../design-system/components/AppPeriodNavigator";
import AppSelect from "../../design-system/components/AppSelect";
import AppTable from "../../design-system/components/AppTable";
import AppTablePane from "../../design-system/components/AppTablePane";
import AppText from "../../design-system/components/AppText";
import AppTextarea from "../../design-system/components/AppTextarea";
import AppToolbar from "../../design-system/components/AppToolbar";
import AppTooltip from "../../design-system/components/AppTooltip";
import AppWorkspace from "../../design-system/components/AppWorkspace";
import {
  IconAdd,
  IconChevronLeft,
  IconChevronRight,
  IconDelete,
  IconEdit,
  IconImport,
  IconRemove,
} from "../../design-system/icons";
import "../../design-system/styles/tokens.css";
import "../../design-system/styles/components.css";
import AvailabilityDateField from "../availability/AvailabilityDateField";
import "../reporting/reporting.css";
import "./reportExpense.css";

const ALL_EMPLOYEES_VALUE = "__all_employees__";

const emptyProjectRow = () => ({
  mode: "existing",
  project_entry_id: "",
  custom_name: "",
  time_min: "",
});

const MAX_PAST_EXPENSE_DAYS = 3;
// Muss zu EXPENSE_TRAVEL_TIME_DEDUCTIBLE_MAX_MINUTES im Backend passen.
const TRAVEL_TIME_DEDUCTIBLE_MAX_MINUTES = 60;
const EXPENSE_TIME_PATTERN = /^(?:[01]\d|2[0-3]):[0-5]\d$/;

function ExpenseDialogSectionHeader({ number, title, subtitle = null }) {
  return (
    <AppFieldStack size="sm">
      <AppToolbar
        left={(
          <>
            <AppChip size="sm" variant="primary" appearance="outlined">{number}</AppChip>
            <AppText as="div" role="heading" aria-level={2} variant="heading-3">
              {title}
            </AppText>
          </>
        )}
      />
      {subtitle ? (
        <AppText as="div" variant="body-2" tone="secondary">
          {subtitle}
        </AppText>
      ) : null}
    </AppFieldStack>
  );
}

function ExpenseTimeInput({ value, onChange, onBlur, onFocus, ...props }) {
  const normalizedValue = String(value ?? "");
  const [draftValue, setDraftValue] = useState(normalizedValue);
  const isFocusedRef = React.useRef(false);

  useEffect(() => {
    if (!isFocusedRef.current) {
      setDraftValue(normalizedValue);
    }
  }, [normalizedValue]);

  const emitChange = (nextValue) => {
    onChange?.({
      target: { value: nextValue },
      currentTarget: { value: nextValue },
    });
  };

  const handleChange = (event) => {
    const nextValue = event.target.value
      .replace(/[^\d:]/g, "")
      .slice(0, 5);

    setDraftValue(nextValue);
    if (nextValue === "" || EXPENSE_TIME_PATTERN.test(nextValue)) {
      emitChange(nextValue);
    }
  };

  const handleFocus = (event) => {
    isFocusedRef.current = true;
    onFocus?.(event);
  };

  const handleBlur = (event) => {
    isFocusedRef.current = false;
    if (!EXPENSE_TIME_PATTERN.test(draftValue)) {
      setDraftValue(normalizedValue);
    }
    onBlur?.(event);
  };

  return (
    <AppInput
      {...props}
      type="text"
      inputMode="numeric"
      placeholder="HH:mm"
      pattern="(?:[01]\d|2[0-3]):[0-5]\d"
      maxLength={5}
      autoComplete="off"
      value={draftValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );
}

const ExpenseReportWeb = () => {
  const { t, i18n } = useTranslation();
  const { success, error } = useToast();
  const token = localStorage.getItem("session_token");

  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();

  const [month, setMonth] = useState(currentMonth);
  const [year, setYear] = useState(currentYear);
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedEntryIds, setSelectedEntryIds] = useState([]);
  const [rows, setRows] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const tablePaneRef = React.useRef(null);
  const [tablePaneWidth, setTablePaneWidth] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [editForm, setEditForm] = useState({
    date: "",
    start_time: "",
    end_time: "",
    pause_time: 0,
    travel_time: 0,
    km_driven: 0,
    dined_out: false,
    note: "",
  });
  const [editProjects, setEditProjects] = useState([emptyProjectRow()]);
  const [lastEditedEditProjectTimeIdx, setLastEditedEditProjectTimeIdx] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    email: "",
    date: "",
    start_time: "",
    end_time: "",
    pause_time: 0,
    travel_time: 0,
    km_driven: 0,
    dined_out: false,
    note: "",
    projects: [emptyProjectRow()],
  });
  const [lastEditedProjectTimeIdx, setLastEditedProjectTimeIdx] = useState(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportTimeframe, setExportTimeframe] = useState({});
  const [exportFilters, setExportFilters] = useState({ employees: [] });
  const [exportPreview, setExportPreview] = useState(null);
  const [exportTabMode, setExportTabMode] = useState("none");
  const [exportResetKey, setExportResetKey] = useState(0);
  const [isExportPreviewLoading, setIsExportPreviewLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const authHeaders = useMemo(
    () => ({
      headers: { Authorization: `Bearer ${token}` },
    }),
    [token]
  );

  const dateLocale = useMemo(
    () => (String(i18n.language || "").toLowerCase().startsWith("de") ? de : enUS),
    [i18n.language]
  );

  const selectedPeriodDate = useMemo(
    () => new Date(year, month - 1, 1),
    [month, year]
  );

  /*
    Die Beschriftung kommt bewusst von hier statt aus formatPeriodTitle des DS:
    diese Seite ist zweisprachig, formatPeriodTitle formatiert fest auf de-CH.
    AppPeriodNavigator sieht periodLabel dafür vor.
  */
  const periodLabel = useMemo(
    () => formatDateFns(selectedPeriodDate, "LLLL yyyy", { locale: dateLocale }),
    [dateLocale, selectedPeriodDate]
  );

  const selectReportPeriod = (date) => {
    setMonth(getMonth(date) + 1);
    setYear(getYear(date));
  };

  const stepReportPeriod = (offset) => selectReportPeriod(addMonths(selectedPeriodDate, offset));

  const formatDateForInput = (dateValue) => {
    const yearPart = dateValue.getFullYear();
    const monthPart = String(dateValue.getMonth() + 1).padStart(2, "0");
    const dayPart = String(dateValue.getDate()).padStart(2, "0");
    return `${yearPart}-${monthPart}-${dayPart}`;
  };

  const getDefaultCreateExpenseValues = () => ({
    date: formatDateForInput(new Date()),
    start_time: "07:00",
    end_time: "16:00",
  });

  const minSelectableExpenseDate = useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() - MAX_PAST_EXPENSE_DAYS);
    return date;
  }, []);

  const projectSelectOptions = useMemo(
    () => [
      ...projectOptions,
      { id: "__custom__", name: t("expense_report_web.create.custom_option") },
    ],
    [projectOptions, t]
  );

  const editPauseOptions = useMemo(() => {
    const values = [];
    for (let i = 0; i <= 8; i += 1) values.push(i * 15);
    return values;
  }, []);

  const createPauseOptions = useMemo(() => {
    const values = [];
    for (let i = 0; i <= 8; i += 1) values.push(i * 15);
    return values;
  }, []);

  const splitTravelMinutes = (travelRaw) => {
    const total = Math.max(0, Number(travelRaw || 0));
    const deductible = Math.min(total, TRAVEL_TIME_DEDUCTIBLE_MAX_MINUTES);
    return { total, deductible, excess: total - deductible };
  };

  // Spiegelt _work_minutes im Backend: Reisezeit wird nur bis zur abzugsfähigen
  // Grenze von der Arbeitszeit abgezogen. Der Überschuss bleibt bezahlt, ist
  // aber bereits dem Projekt "Reisezeit" zugeteilt und damit nicht mehr frei
  // auf Baustellen verteilbar.
  const calcExpenseTimes = (dateStr, startStr, endStr, pauseRaw, travelRaw) => {
    if (!dateStr || !startStr || !endStr) return null;
    const pause = Number(pauseRaw || 0);
    if (!Number.isFinite(pause) || pause < 0) return null;
    const travelRawNum = Number(travelRaw || 0);
    if (!Number.isFinite(travelRawNum) || travelRawNum < 0) return null;

    const start = new Date(`${dateStr}T${startStr}:00`);
    const end = new Date(`${dateStr}T${endStr}:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;

    const diffMin = Math.floor((end.getTime() - start.getTime()) / 60000);
    if (diffMin <= 0) return null;

    const { deductible, excess } = splitTravelMinutes(travelRawNum);
    const worked = diffMin - pause - deductible;
    const distributable = worked - excess;
    if (distributable < 0) return null;

    return { gross: diffMin, worked, distributable, travelExcess: excess };
  };

  const formatMinutesHm = (minutes) => {
    const mins = Math.max(0, Number(minutes || 0));
    const hh = Math.floor(mins / 60);
    const mm = mins % 60;
    return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
  };

  const formatMinutesForTimeInput = (minutes) => {
    const mins = Math.max(0, Math.min(1439, Number(minutes || 0)));
    const hh = Math.floor(mins / 60);
    const mm = mins % 60;
    return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
  };

  const parseTimeInputToMinutes = (value) => {
    const raw = (value || "").trim();
    if (!raw) return 0;
    const parts = raw.split(":");
    if (parts.length !== 2) return 0;

    const hh = Number(parts[0]);
    const mm = Number(parts[1]);
    if (!Number.isInteger(hh) || !Number.isInteger(mm)) return 0;
    if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return 0;

    return (hh * 60) + mm;
  };

  const getProjectsDisplay = (row) => {
    const projects = row?.projects || [];
    if (!projects.length) return "-";
    if (projects.length === 1) {
      return `${projects[0].name} (${row?.net_hm || projects[0].time_hm || "00:00"})`;
    }
    return projects.map((p) => `${p.name} (${p.time_hm})`).join("\n");
  };

  const getNoteDisplay = (row) => {
    const raw = String(row?.note || "").trim();
    if (!raw) return "-";

    const projects = row?.projects || [];
    const normalizedParts = raw
      .split(/\n\s*\n/)
      .map((part) => part.trim())
      .filter(Boolean);

    if (projects.length <= 1 || normalizedParts.length <= 1) {
      return raw.replace(/\n{2,}/g, "\n");
    }

    const remainingParts = [...normalizedParts];
    const orderedParts = [];

    projects.forEach((project) => {
      const projectName = String(project?.name || "").trim();
      if (!projectName) return;

      const prefix = `${projectName}:`;
      const matchIndex = remainingParts.findIndex((part) => part.toLowerCase().startsWith(prefix.toLowerCase()));
      if (matchIndex >= 0) {
        orderedParts.push(remainingParts[matchIndex]);
        remainingParts.splice(matchIndex, 1);
      }
    });

    const finalParts = [...orderedParts, ...remainingParts];
    return finalParts.join("\n");
  };

  useEffect(() => {
    let mounted = true;

    const fetchReports = async () => {
      setIsLoading(true);
      try {
        const params = { month, year };
        if (selectedUser) params.selectedUser = selectedUser;

        const res = await axios.get(`${API_BASE_URL}/api/web/expense-reporting/reports`, {
          ...authHeaders,
          params,
        });

        if (!mounted) return;
        setRows(res.data?.reports || []);
        setSelectedEntryIds([]);
        setUserOptions(res.data?.user_options || []);
        setIsAdmin(Boolean(res.data?.is_admin));
      } catch (err) {
        if (mounted) error(t("expense_report_web.toast.load_error"));
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    fetchReports();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, year, selectedUser, reloadTick, token]);

  const fetchProjectOptions = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/web/expense-reporting/projects`, authHeaders);
      setProjectOptions(res.data?.projects || []);
    } catch (err) {
      error(t("expense_report_web.toast.projects_load_error"));
    }
  };

  const openEditDialog = async (row) => {
    if (projectOptions.length === 0) await fetchProjectOptions();

    // Die "Reisezeit"-Buchung wird automatisch gepflegt und darf nicht als
    // wählbare Zeile ins Formular wandern.
    const mappedProjects = (row.projects || [])
      .filter((p) => !p.is_system)
      .map((p) => ({
        mode: p.is_custom ? "custom" : "existing",
        project_entry_id: p.is_custom ? "" : Number(p.project_entry_id || ""),
        custom_name: p.is_custom ? p.name || "" : "",
        time_min: Number(p.time_min || 0),
      }));

    setEditingRow(row);
    setEditForm({
      date: row.date || "",
      start_time: row.start_time || "",
      end_time: row.end_time || "",
      pause_time: Number(row.pause_time || 0),
      travel_time: Number(row.travel_time || 0),
      km_driven: Number(row.km_driven || 0),
      dined_out: Boolean(row.dined_out),
      note: row.note || "",
    });
    setEditProjects(mappedProjects.length ? mappedProjects : [emptyProjectRow()]);
    setLastEditedEditProjectTimeIdx(null);
    setEditOpen(true);
  };

  const closeEditDialog = () => {
    if (isSaving) return;
    setEditOpen(false);
    setEditingRow(null);
    setEditProjects([emptyProjectRow()]);
    setLastEditedEditProjectTimeIdx(null);
  };

  const saveEditDialog = async () => {
    if (!editingRow) return;
    if (!editForm.date || !editForm.start_time || !editForm.end_time) {
      error(t("expense_report_web.toast.validation_error"));
      return;
    }
    const editTravel = Number(editForm.travel_time || 0);
    if (!Number.isFinite(editTravel) || editTravel < 0 || !Number.isInteger(editTravel)) {
      error(t("expense_report_web.toast.travel_time_error"));
      return;
    }
    if (editTimes === null) {
      error(t("expense_report_web.toast.time_error"));
      return;
    }

    const projectsPayload = [];
    for (let i = 0; i < editProjects.length; i += 1) {
      const row = editProjects[i];
      if (row.mode === "custom") {
        const name = (row.custom_name || "").trim();
        if (!name) {
          error(t("expense_report_web.toast.project_required"));
          return;
        }
        projectsPayload.push({
          is_custom: true,
          name,
          ...(editProjects.length > 1 ? { time_min: Number(row.time_min || 0) } : {}),
        });
      } else {
        const idNum = Number(row.project_entry_id || 0);
        if (!idNum) {
          error(t("expense_report_web.toast.project_required"));
          return;
        }
        projectsPayload.push({
          project_entry_id: idNum,
          ...(editProjects.length > 1 ? { time_min: Number(row.time_min || 0) } : {}),
        });
      }
    }

    if (editProjects.length > 1) {
      const sum = projectsPayload.reduce((acc, p) => acc + Number(p.time_min || 0), 0);
      if (sum !== distributableEditMinutes) {
        error(t("expense_report_web.toast.project_time_sum_error", { worked: formatMinutesHm(distributableEditMinutes) }));
        return;
      }
    }

    setIsSaving(true);
    try {
      await axios.put(
        `${API_BASE_URL}/api/web/expense-reporting/reports/${editingRow.id}`,
        {
          date: editForm.date,
          start_time: editForm.start_time,
          end_time: editForm.end_time,
          pause_time: Number(editForm.pause_time || 0),
          travel_time: Number(editForm.travel_time || 0),
          km_driven: Number(editForm.km_driven || 0),
          dined_out: Boolean(editForm.dined_out),
          note: editForm.note || "",
          projects: projectsPayload,
        },
        authHeaders
      );

      success(t("expense_report_web.toast.update_success"));
      setEditOpen(false);
      setEditingRow(null);
      setReloadTick((v) => v + 1);
    } catch (err) {
      error(err?.response?.data?.msg || t("expense_report_web.toast.update_error"));
    } finally {
      setIsSaving(false);
    }
  };

  const openCreateDialog = async () => {
    if (projectOptions.length === 0) await fetchProjectOptions();
    const defaults = getDefaultCreateExpenseValues();

    setCreateForm({
      email: isAdmin ? selectedUser || "" : "",
      date: defaults.date,
      start_time: defaults.start_time,
      end_time: defaults.end_time,
      pause_time: 0,
      travel_time: 0,
      km_driven: 0,
      dined_out: false,
      note: "",
      projects: [emptyProjectRow()],
    });
    setLastEditedProjectTimeIdx(null);
    setCreateOpen(true);
  };

  const closeCreateDialog = () => {
    if (isSaving) return;
    setCreateOpen(false);
    setLastEditedProjectTimeIdx(null);
  };

  const updateCreateProjectRow = (idx, patch, options = {}) => {
    const { editedTime = false } = options;
    if (editedTime) setLastEditedProjectTimeIdx(idx);

    setCreateForm((prev) => {
      const next = [...prev.projects];
      next[idx] = { ...next[idx], ...patch };

      if (editedTime && next.length === 2 && distributableCreateMinutes !== null) {
        const thisRaw = Number(next[idx].time_min || 0);
        const thisVal = Number.isFinite(thisRaw) ? Math.max(0, Math.min(distributableCreateMinutes, thisRaw)) : 0;
        const otherIdx = idx === 0 ? 1 : 0;
        next[idx] = { ...next[idx], time_min: thisVal };
        next[otherIdx] = { ...next[otherIdx], time_min: distributableCreateMinutes - thisVal };
      }

      return { ...prev, projects: next };
    });
  };

  const addCreateProjectRow = () => {
    setCreateForm((prev) => {
      if (prev.projects.length >= 3) return prev;
      return { ...prev, projects: [...prev.projects, emptyProjectRow()] };
    });
    setLastEditedProjectTimeIdx(null);
  };

  const removeCreateProjectRow = (idx) => {
    setCreateForm((prev) => {
      const next = prev.projects.filter((_, i) => i !== idx);
      return { ...prev, projects: next.length ? next : [emptyProjectRow()] };
    });
    setLastEditedProjectTimeIdx(null);
  };

  const updateEditProjectRow = (idx, patch, options = {}) => {
    const { editedTime = false } = options;
    if (editedTime) setLastEditedEditProjectTimeIdx(idx);

    setEditProjects((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };

      if (editedTime && next.length === 2 && distributableEditMinutes !== null) {
        const thisRaw = Number(next[idx].time_min || 0);
        const thisVal = Number.isFinite(thisRaw) ? Math.max(0, Math.min(distributableEditMinutes, thisRaw)) : 0;
        const otherIdx = idx === 0 ? 1 : 0;
        next[idx] = { ...next[idx], time_min: thisVal };
        next[otherIdx] = { ...next[otherIdx], time_min: distributableEditMinutes - thisVal };
      }

      return next;
    });
  };

  const addEditProjectRow = () => {
    setEditProjects((prev) => {
      if (prev.length >= 3) return prev;
      return [...prev, emptyProjectRow()];
    });
    setLastEditedEditProjectTimeIdx(null);
  };

  const removeEditProjectRow = (idx) => {
    setEditProjects((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      return next.length ? next : [emptyProjectRow()];
    });
    setLastEditedEditProjectTimeIdx(null);
  };

  const saveCreateDialog = async () => {
    if (isAdmin && !createForm.email) {
      error(t("expense_report_web.toast.employee_required"));
      return;
    }
    if (!createForm.date || !createForm.start_time || !createForm.end_time) {
      error(t("expense_report_web.toast.validation_error"));
      return;
    }

    const pause = Number(createForm.pause_time || 0);
    if (pause % 15 !== 0 || pause > 120) {
      error(t("expense_report_web.toast.pause_step_error"));
      return;
    }

    const travel = Number(createForm.travel_time || 0);
    if (!Number.isFinite(travel) || travel < 0 || !Number.isInteger(travel)) {
      error(t("expense_report_web.toast.travel_time_error"));
      return;
    }

    const times = calcExpenseTimes(
      createForm.date,
      createForm.start_time,
      createForm.end_time,
      createForm.pause_time,
      travel
    );
    if (times === null) {
      error(t("expense_report_web.toast.time_error"));
      return;
    }
    const distributableMinutes = times.distributable;

    const projectsPayload = [];
    for (let i = 0; i < createForm.projects.length; i += 1) {
      const row = createForm.projects[i];
      if (row.mode === "custom") {
        const name = (row.custom_name || "").trim();
        if (!name) {
          error(t("expense_report_web.toast.project_required"));
          return;
        }
        projectsPayload.push({
          is_custom: true,
          name,
          ...(createForm.projects.length > 1 ? { time_min: Number(row.time_min || 0) } : {}),
        });
      } else {
        const idNum = Number(row.project_entry_id || 0);
        if (!idNum) {
          error(t("expense_report_web.toast.project_required"));
          return;
        }
        projectsPayload.push({
          project_entry_id: idNum,
          ...(createForm.projects.length > 1 ? { time_min: Number(row.time_min || 0) } : {}),
        });
      }
    }

    if (createForm.projects.length > 1) {
      const sum = projectsPayload.reduce((acc, p) => acc + Number(p.time_min || 0), 0);
      if (sum !== distributableMinutes) {
        error(t("expense_report_web.toast.project_time_sum_error", { worked: formatMinutesHm(distributableMinutes) }));
        return;
      }
    }

    setIsSaving(true);
    try {
      await axios.post(
        `${API_BASE_URL}/api/web/expense-reporting/reports`,
        {
          date: createForm.date,
          start_time: createForm.start_time,
          end_time: createForm.end_time,
          pause_time: pause,
          travel_time: travel,
          km_driven: Number(createForm.km_driven || 0),
          dined_out: Boolean(createForm.dined_out),
          note: createForm.note || "",
          projects: projectsPayload,
          ...(isAdmin ? { email: createForm.email } : {}),
        },
        authHeaders
      );

      success(t("expense_report_web.toast.create_success"));
      setCreateOpen(false);
      setReloadTick((v) => v + 1);
    } catch (err) {
      error(err?.response?.data?.msg || t("expense_report_web.toast.create_error"));
    } finally {
      setIsSaving(false);
    }
  };

  const openDeleteDialog = () => {
    if (selectedEntryIds.length === 0) return;
    setDeleteOpen(true);
  };

  const closeDeleteDialog = () => {
    if (isSaving) return;
    setDeleteOpen(false);
  };

  const confirmDelete = async () => {
    if (isSaving || selectedEntryIds.length === 0) return;
    const idsToDelete = [...selectedEntryIds];
    setIsSaving(true);
    try {
      await Promise.all(
        idsToDelete.map((id) =>
          axios.delete(
            `${API_BASE_URL}/api/web/expense-reporting/reports/${id}`,
            authHeaders
          )
        )
      );
      success(t("expense_report_web.toast.delete_success"));
      setDeleteOpen(false);
      setSelectedEntryIds([]);
      setReloadTick((v) => v + 1);
    } catch (err) {
      error(err?.response?.data?.msg || t("expense_report_web.toast.delete_error"));
    } finally {
      setIsSaving(false);
    }
  };

  const closeExportDialog = () => {
    if (isExporting) return;
    setExportOpen(false);
  };

  const buildExportParams = (
    filters = exportFilters,
    timeframe = exportTimeframe,
    tabMode = exportTabMode,
  ) => {
    const params = { tab_mode: tabMode };
    if (timeframe?.mode === "range") {
      if (!timeframe.start_date || !timeframe.end_date || timeframe.end_date < timeframe.start_date) {
        return null;
      }
      params.start_date = timeframe.start_date;
      params.end_date = timeframe.end_date;
    } else {
      params.month = timeframe?.month || `${year}-${String(month).padStart(2, "0")}`;
    }
    if (filters?.employees?.length) {
      params.employee_emails = filters.employees.join(",");
    }
    return params;
  };

  const loadExportPreview = async (
    filters = exportFilters,
    timeframe = exportTimeframe,
    tabMode = exportTabMode,
  ) => {
    const params = buildExportParams(filters, timeframe, tabMode);
    if (!params) return null;
    setIsExportPreviewLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/web/expense-reporting/reports/export/preview`,
        {
          params,
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setExportPreview(response.data);
      return response.data;
    } catch (err) {
      error(err?.response?.data?.msg || t("expense_report_web.toast.export_error", "Vorschau konnte nicht geladen werden."));
      return null;
    } finally {
      setIsExportPreviewLoading(false);
    }
  };

  const openExportDialog = async () => {
    const nextTimeframe = {
      mode: "month",
      month: `${year}-${String(month).padStart(2, "0")}`,
      start_date: "",
      end_date: "",
    };
    const nextFilters = { employees: selectedUser ? [selectedUser] : [] };
    setExportPreview(null);
    setExportTimeframe(nextTimeframe);
    setExportFilters(nextFilters);
    setExportTabMode("none");
    setExportOpen(true);
    const preview = await loadExportPreview(nextFilters, nextTimeframe, "none");
    if (!preview) return;
    setExportResetKey((current) => current + 1);
  };

  const handleExportTimeframeChange = (nextTimeframe) => {
    setExportTimeframe(nextTimeframe);
    loadExportPreview(exportFilters, nextTimeframe).catch(() => {});
  };

  const handleExportFiltersChange = (nextFilters) => {
    setExportFilters(nextFilters);
    loadExportPreview(nextFilters, exportTimeframe).catch(() => {});
  };

  const handleExpenseDownload = async ({
    format: exportFormat = "excel",
    columns: exportColumns,
    tab_mode: tabMode,
    filters,
    timeframe,
    settings,
  }) => {
    if (!buildExportParams(filters, timeframe)) {
      error(t("expense_report_web.toast.export_range_error", "Bitte einen gültigen Zeitraum wählen."));
      return;
    }
    const isPdfExport = exportFormat === "pdf";
    const extension = isPdfExport ? "pdf" : "xlsx";
    const fileSuffix = timeframe?.mode === "range"
      ? `${timeframe.start_date}${timeframe.end_date !== timeframe.start_date ? `_bis_${timeframe.end_date}` : ""}`
      : (timeframe?.month || `${year}-${String(month).padStart(2, "0")}`);
    const payload = {
      format: exportFormat,
      columns: exportColumns,
      tab_mode: tabMode,
      timeframe,
      settings,
      employee_emails: filters?.employees?.length ? filters.employees : undefined,
    };

    setIsExporting(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/web/expense-reporting/reports/export`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], {
        type: isPdfExport
          ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.setAttribute(
        "download",
        downloadFilenameFromDisposition(
          response.headers?.["content-disposition"],
          `Spesenbericht_${fileSuffix}.${extension}`,
        ),
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(link.href);

      success(t("expense_report_web.toast.export_success", "Download erfolgreich erstellt."));
      setExportOpen(false);
    } catch (err) {
      error(err?.response?.data?.msg || t("expense_report_web.toast.export_error", "Download konnte nicht erstellt werden."));
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    if (isLoading) return undefined;
    const pane = tablePaneRef.current;
    if (!pane) return undefined;

    const measure = () => {
      const nextWidth = Math.round(pane.getBoundingClientRect().width);
      setTablePaneWidth((currentWidth) =>
        currentWidth === nextWidth ? currentWidth : nextWidth
      );
    };

    measure();
    window.addEventListener("resize", measure);
    window.visualViewport?.addEventListener("resize", measure);
    const observer = typeof ResizeObserver === "undefined"
      ? null
      : new ResizeObserver(measure);
    observer?.observe(pane);

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
      window.visualViewport?.removeEventListener("resize", measure);
    };
  }, [isLoading]);

  const mergeEmployeeName = tablePaneWidth !== null && tablePaneWidth < 1500;
  const mergeDateAndTimes = tablePaneWidth !== null && tablePaneWidth < 1360;
  const mergeWorkAndPause = tablePaneWidth !== null && tablePaneWidth < 1180;

  const columns = [
    isAdmin && {
      key: "selected",
      label: "",
      controlKind: "selection",
      sortable: false,
    },
    !mergeEmployeeName && {
      key: "first_name",
      label: t("expense_report_web.table.first_name"),
      width: 110,
      render: (value) => (
        <AppText variant="heading-4" truncate>{value || "–"}</AppText>
      ),
    },
    !mergeEmployeeName && {
      key: "last_name",
      label: t("expense_report_web.table.last_name"),
      width: 120,
      render: (value) => (
        <AppText variant="heading-4" truncate>{value || "–"}</AppText>
      ),
    },
    mergeEmployeeName && {
      key: "employee_name",
      label: t("expense_report_web.table.name"),
      width: 150,
      render: (value) => (
        <AppText variant="heading-4" truncate>{value || "–"}</AppText>
      ),
    },
    !mergeDateAndTimes && {
      key: "date_label",
      label: t("expense_report_web.table.date"),
      width: 100,
    },
    !mergeDateAndTimes && {
      key: "start_time",
      label: t("expense_report_web.table.start"),
      width: 80,
    },
    !mergeDateAndTimes && {
      key: "end_time",
      label: t("expense_report_web.table.end"),
      width: 80,
    },
    mergeDateAndTimes && {
      key: "date_time",
      label: t("expense_report_web.table.date_time"),
      width: 145,
      sortAccessor: (row) => `${row.date || ""} ${row.start_time || ""}`,
      render: (_value, row) => (
        <div className="expense-table__stacked-value">
          <AppText variant="body-1">{row.date_label || "–"}</AppText>
          <AppText variant="body-2" tone="secondary">
            {[row.start_time, row.end_time].filter(Boolean).join("–") || "–"}
          </AppText>
        </div>
      ),
    },
    !mergeWorkAndPause && {
      key: "pause_time",
      label: t("expense_report_web.table.pause"),
      width: 80,
    },
    // Zwischen Pause und Arbeitszeit, damit sich die Spalten als die Rechnung
    // lesen: brutto, minus Pause, minus Reisezeit, ergibt Arbeitszeit.
    !mergeWorkAndPause && {
      key: "travel_time",
      label: t("expense_report_web.table.travel_time"),
      width: 90,
    },
    !mergeWorkAndPause && {
      key: "net_hm",
      label: t("expense_report_web.table.work_time"),
      width: 100,
    },
    mergeWorkAndPause && {
      key: "work_pause",
      label: t("expense_report_web.table.work_pause"),
      width: 115,
      sortAccessor: (row) => Number(row.net_minutes || 0),
      render: (_value, row) => (
        <div className="expense-table__stacked-value">
          <AppText variant="body-1">{row.net_hm || "–"}</AppText>
          <AppText variant="body-2" tone="secondary">
            {t("expense_report_web.table.pause_short", {
              minutes: Number(row.pause_time || 0),
            })}
          </AppText>
          {Number(row.travel_time || 0) > 0 ? (
            <AppText variant="body-2" tone="secondary">
              {t("expense_report_web.table.travel_short", {
                minutes: Number(row.travel_time || 0),
              })}
            </AppText>
          ) : null}
        </div>
      ),
    },
    { key: "km_driven", label: t("expense_report_web.table.km"), width: 70 },
    {
      key: "dined_out",
      label: t("expense_report_web.table.dined_out"),
      width: 90,
      align: "center",
      render: (value) => (
        <AppCheckbox checked={Boolean(value)} readOnly aria-label={t("expense_report_web.table.dined_out")} />
      ),
    },
    {
      key: "projects",
      label: t("expense_report_web.table.projects"),
      minWidth: 240,
      grow: 3,
      sortable: false,
      render: (value, row) => {
        const text = getProjectsDisplay(row);
        const showMultiIcon = (row.projects || []).length > 1;
        return (
          <AppTooltip content={text}>
            <div className="expense-table__multiline">
              {showMultiIcon && <AccountTreeIcon fontSize="small" sx={{ color: "warning.main" }} />}
              <AppText variant="body-1">{text}</AppText>
            </div>
          </AppTooltip>
        );
      },
    },
    {
      key: "note",
      label: t("expense_report_web.table.note", "Kommentar"),
      minWidth: 280,
      grow: 2.5,
      sortable: false,
      render: (value, row) => {
        const text = getNoteDisplay(row);
        const showMultiIcon = (row.projects || []).length > 1;
        if (text === "-") return <AppText variant="body-1" tone="secondary">-</AppText>;
        return (
          <AppTooltip content={text}>
            <div className="expense-table__multiline">
              {showMultiIcon && <AccountTreeIcon fontSize="small" sx={{ color: "warning.main", mt: 0.15 }} />}
              <AppText variant="body-1">{text}</AppText>
            </div>
          </AppTooltip>
        );
      },
    },
    {
      key: "edit",
      label: "",
      controlKind: "action",
      sortable: false,
      render: (value, row) =>
        isAdmin ? (
          <AppTooltip content={t("button.edit", "Bearbeiten")}>
            <AppButton variant="neutral" size="sm" iconOnly onClick={() => openEditDialog(row)}><IconEdit /></AppButton>
          </AppTooltip>
        ) : null,
    },
  ].filter(Boolean);

  const employeeNavigationOptions = useMemo(
    () => [
      {
        value: ALL_EMPLOYEES_VALUE,
        label: t("expense_report_web.filters.all_employees"),
      },
      ...userOptions.map((user) => ({ value: user.email, label: user.label })),
    ],
    [t, userOptions]
  );
  const selectedEmployeeValue = selectedUser || ALL_EMPLOYEES_VALUE;
  const selectedEmployeeIndex = employeeNavigationOptions.findIndex(
    (option) => option.value === selectedEmployeeValue
  );
  const selectEmployeeAt = (index) => {
    const option = employeeNavigationOptions[index];
    if (!option) return;
    setSelectedUser(option.value === ALL_EMPLOYEES_VALUE ? "" : option.value);
  };

  const toolbarLeft = (
    <>
      <div className="reporting-toolbar__period">
        {/* Dieselbe Navigation wie auf /availability, /demand und /shiftplan. */}
        <AppPeriodNavigator
          viewMode="month"
          referenceDate={selectedPeriodDate}
          periodLabel={periodLabel}
          size="md"
          className="reporting-toolbar__period-navigator"
          previousLabel={t("availability.previous_month")}
          nextLabel={t("availability.next_month")}
          onPreviousPeriod={() => stepReportPeriod(-1)}
          onNextPeriod={() => stepReportPeriod(1)}
          onSelectDate={selectReportPeriod}
        />
      </div>
      {isAdmin ? (
        <>
          <AppTooltip content={t("report.download")}>
            <AppButton
              variant="neutral"
              size="md"
              iconOnly
              onClick={openExportDialog}
              disabled={isExporting || isExportPreviewLoading}
              aria-label={t("report.download")}
            >
              <IconImport />
            </AppButton>
          </AppTooltip>
          <AppTooltip content={t("expense_report_web.delete.delete_selected")}>
            <AppButton
              variant={selectedEntryIds.length > 0 ? "error" : "muted"}
              size="md"
              iconOnly
              onClick={openDeleteDialog}
              disabled={selectedEntryIds.length === 0}
              aria-label={t("expense_report_web.delete.delete_selected")}
            >
              <IconDelete />
            </AppButton>
          </AppTooltip>
        </>
      ) : null}
    </>
  );

  const toolbarRight = (
    <div className="expense-toolbar__right">
      {isAdmin ? (
        <div className="reporting-toolbar__employee-navigation">
          <AppTooltip content={t("team.previous_user")}>
            <AppButton
              variant="neutral"
              size="sm"
              iconOnly
              aria-label={t("team.previous_user")}
              onClick={() => selectEmployeeAt(selectedEmployeeIndex - 1)}
              disabled={selectedEmployeeIndex <= 0}
            >
              <IconChevronLeft />
            </AppButton>
          </AppTooltip>
          <div className="reporting-toolbar__user">
            <AppSelect
              size="md"
              value={selectedEmployeeValue}
              options={employeeNavigationOptions}
              onChange={(event) => setSelectedUser(
                event.target.value === ALL_EMPLOYEES_VALUE ? "" : event.target.value
              )}
              disabled={employeeNavigationOptions.length <= 1}
              searchable
              searchPlaceholder={t("team.select_user")}
              placeholder={t("team.select_user")}
              aria-label={t("expense_report_web.filters.employee")}
            />
          </div>
          <AppTooltip content={t("team.next_user")}>
            <AppButton
              variant="neutral"
              size="sm"
              iconOnly
              aria-label={t("team.next_user")}
              onClick={() => selectEmployeeAt(selectedEmployeeIndex + 1)}
              disabled={
                selectedEmployeeIndex < 0 ||
                selectedEmployeeIndex >= employeeNavigationOptions.length - 1
              }
            >
              <IconChevronRight />
            </AppButton>
          </AppTooltip>
        </div>
      ) : null}
      <AppTooltip content={t("expense_report_web.actions.entry_create_short")}>
        <AppButton
          variant="primary"
          size="md"
          iconOnly
          aria-label={t("expense_report_web.actions.entry_create_short")}
          onClick={openCreateDialog}
        >
          <IconAdd />
        </AppButton>
      </AppTooltip>
    </div>
  );

  const createTimes = calcExpenseTimes(
    createForm.date,
    createForm.start_time,
    createForm.end_time,
    createForm.pause_time,
    createForm.travel_time
  );
  const editTimes = calcExpenseTimes(
    editForm.date,
    editForm.start_time,
    editForm.end_time,
    editForm.pause_time,
    editForm.travel_time
  );
  const workedCreateMinutes = createTimes ? createTimes.worked : null;
  const workedEditMinutes = editTimes ? editTimes.worked : null;
  // Auf Projekte verteilt wird nur der Teil, der nicht schon der Reisezeit gehört.
  const distributableCreateMinutes = createTimes ? createTimes.distributable : null;
  const distributableEditMinutes = editTimes ? editTimes.distributable : null;
  const createTravelExcess = createTimes ? createTimes.travelExcess : 0;
  const editTravelExcess = editTimes ? editTimes.travelExcess : 0;

  const hasCreateTimeFields = Boolean(createForm.date && createForm.start_time && createForm.end_time);
  const canDefineProjects = Boolean(hasCreateTimeFields && distributableCreateMinutes !== null);
  const canEditProjects = Boolean(editForm.date && editForm.start_time && editForm.end_time && distributableEditMinutes !== null);
  const showCreateBaseValidation = !hasCreateTimeFields;
  const showCreateTimeError = hasCreateTimeFields && workedCreateMinutes === null;
  const showWorkedCreateHint = Boolean(createForm.date && createForm.start_time && createForm.end_time);

  useEffect(() => {
    if (!createOpen || createForm.projects.length !== 2 || distributableCreateMinutes === null || lastEditedProjectTimeIdx === null) {
      return;
    }

    setCreateForm((prev) => {
      if (prev.projects.length !== 2) return prev;

      const sourceIdx = lastEditedProjectTimeIdx === 1 ? 1 : 0;
      const otherIdx = sourceIdx === 0 ? 1 : 0;
      const sourceRaw = Number(prev.projects[sourceIdx].time_min || 0);
      const sourceVal = Number.isFinite(sourceRaw)
        ? Math.max(0, Math.min(distributableCreateMinutes, sourceRaw))
        : 0;
      const otherVal = distributableCreateMinutes - sourceVal;

      if (
        Number(prev.projects[sourceIdx].time_min) === sourceVal &&
        Number(prev.projects[otherIdx].time_min) === otherVal
      ) {
        return prev;
      }

      const next = [...prev.projects];
      next[sourceIdx] = { ...next[sourceIdx], time_min: sourceVal };
      next[otherIdx] = { ...next[otherIdx], time_min: otherVal };
      return { ...prev, projects: next };
    });
  }, [createOpen, createForm.projects.length, distributableCreateMinutes, lastEditedProjectTimeIdx]);

  useEffect(() => {
    if (!editOpen || editProjects.length !== 2 || distributableEditMinutes === null || lastEditedEditProjectTimeIdx === null) {
      return;
    }

    setEditProjects((prev) => {
      if (prev.length !== 2) return prev;

      const sourceIdx = lastEditedEditProjectTimeIdx === 1 ? 1 : 0;
      const otherIdx = sourceIdx === 0 ? 1 : 0;
      const sourceRaw = Number(prev[sourceIdx].time_min || 0);
      const sourceVal = Number.isFinite(sourceRaw)
        ? Math.max(0, Math.min(distributableEditMinutes, sourceRaw))
        : 0;
      const otherVal = distributableEditMinutes - sourceVal;

      if (
        Number(prev[sourceIdx].time_min) === sourceVal &&
        Number(prev[otherIdx].time_min) === otherVal
      ) {
        return prev;
      }

      const next = [...prev];
      next[sourceIdx] = { ...next[sourceIdx], time_min: sourceVal };
      next[otherIdx] = { ...next[otherIdx], time_min: otherVal };
      return next;
    });
  }, [editOpen, editProjects.length, distributableEditMinutes, lastEditedEditProjectTimeIdx]);

  return (
    <div className="expense-page">
      <AppWorkspace title={t("expense_report_web.title")} loading={isLoading}>
        <AppToolbar
          className="reporting-toolbar"
          left={toolbarLeft}
          right={toolbarRight}
        />
        <AppTablePane ref={tablePaneRef} className="expense-table-pane">
          <AppTable
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            selectedRowIds={selectedEntryIds}
            onSelectedRowIdsChange={setSelectedEntryIds}
            isRowSelectable={() => isAdmin}
            emptyState={t("expense_report_web.table.no_rows")}
          />
        </AppTablePane>
      </AppWorkspace>

      <AppDialog open={editOpen} onClose={isSaving ? undefined : closeEditDialog} size="lg">
        <AppDialogHeader title={t("expense_report_web.dialog.entry_title", t("expense_report_web.dialog.title"))} />
        <AppDialogBody>
          {editingRow ? (
            <>
              <AppFieldStack size="md">
                <ExpenseDialogSectionHeader
                  number="1"
                  title={t("expense_report_web.create.expense_data_title")}
                />
                <AppField label={t("expense_report_web.table.employee")}>
                  <AppInput value={editingRow.employee_name || ""} readOnly />
                </AppField>
                <AppFieldGrid columns={3} size="md">
                <AvailabilityDateField
                  label={t("expense_report_web.table.date")}
                  value={editForm.date}
                  required
                  onChange={(value) => setEditForm((prev) => ({ ...prev, date: value }))}
                />
                <AppField label={t("expense_report_web.table.start")} required>
                  <ExpenseTimeInput
                    value={editForm.start_time || ""}
                    onChange={(event) => setEditForm((prev) => ({ ...prev, start_time: event.target.value }))}
                  />
                </AppField>
                <AppField label={t("expense_report_web.table.end")} required>
                  <ExpenseTimeInput
                    value={editForm.end_time || ""}
                    onChange={(event) => setEditForm((prev) => ({ ...prev, end_time: event.target.value }))}
                  />
                </AppField>
                </AppFieldGrid>
                <AppFieldGrid columns={4} size="md">
                <AppField label={t("expense_report_web.table.pause")}>
                  <AppSelect
                    value={editForm.pause_time}
                    onChange={(event) => setEditForm((prev) => ({ ...prev, pause_time: Number(event.target.value) }))}
                    options={editPauseOptions.map((value) => ({ value, label: String(value) }))}
                  />
                </AppField>
                <AppField
                  label={t("expense_report_web.create.travel_time")}
                  help={t("expense_report_web.create.travel_time_help")}
                >
                  <AppInput
                    type="number"
                    min="0"
                    step="1"
                    value={editForm.travel_time}
                    onChange={(event) => setEditForm((prev) => ({ ...prev, travel_time: event.target.value }))}
                  />
                </AppField>
                <AppField label={t("expense_report_web.table.km")}>
                  <AppInput
                    type="number"
                    min="0"
                    step="1"
                    value={editForm.km_driven}
                    onChange={(event) => setEditForm((prev) => ({ ...prev, km_driven: event.target.value }))}
                  />
                </AppField>
                <AppField label={t("expense_report_web.table.dined_out")}>
                  <AppSelect
                    value={editForm.dined_out ? "yes" : "no"}
                    onChange={(event) => setEditForm((prev) => ({ ...prev, dined_out: event.target.value === "yes" }))}
                    options={[
                      { value: "yes", label: t("expense_report_web.boolean.yes") },
                      { value: "no", label: t("expense_report_web.boolean.no") },
                    ]}
                  />
                </AppField>
                </AppFieldGrid>
                <AppField
                  label={t("expense_report_web.table.note", "Kommentar")}
                  help={`${(editForm.note || "").length}/500`}
                >
                  <AppTextarea
                    value={editForm.note || ""}
                    rows={3}
                    onChange={(event) => setEditForm((prev) => ({ ...prev, note: event.target.value.slice(0, 500) }))}
                  />
                </AppField>
              </AppFieldStack>
              <AppDivider spacing="lg" />
              <AppFieldStack size="md">
                <ExpenseDialogSectionHeader
                  number="2"
                  title={t("expense_report_web.create.project_allocation_title")}
                  subtitle={workedEditMinutes !== null
                    ? (editTravelExcess > 0
                      ? t("expense_report_web.create.distributable_time_hint", {
                        worked: formatMinutesHm(workedEditMinutes),
                        distributable: formatMinutesHm(distributableEditMinutes),
                      })
                      : t("expense_report_web.create.worked_time_hint", { worked: formatMinutesHm(workedEditMinutes) }))
                    : null}
                />
                {!canEditProjects ? (
                  <AppText as="p" variant="body-2" tone="secondary">
                    {t("expense_report_web.toast.validation_error")}
                  </AppText>
                ) : null}
                {editProjects.map((project, index) => {
                  const selectedValue = project.mode === "custom" ? "__custom__" : project.project_entry_id;
                  return (
                    <AppFieldGrid key={`edit-project-${index}`} columns={4} size="sm">
                      <AppField
                        label={t("expense_report_web.create.project")}
                        span={editProjects.length > 1
                          ? (project.mode === "custom" ? 2 : 3)
                          : (project.mode === "custom" ? 2 : 4)}
                      >
                        <AppSelect
                          searchable
                          value={selectedValue}
                          disabled={!canEditProjects}
                          options={projectSelectOptions.map((option) => ({ value: option.id, label: option.name }))}
                          onChange={(event) => {
                            const value = event.target.value;
                            updateEditProjectRow(index, value === "__custom__"
                              ? { mode: "custom", project_entry_id: "" }
                              : { mode: "existing", project_entry_id: value, custom_name: "" });
                          }}
                        />
                      </AppField>
                      {project.mode === "custom" ? (
                        <AppField
                          label={t("expense_report_web.create.custom_name")}
                          span={editProjects.length > 1 ? 1 : 2}
                        >
                          <AppInput
                            value={project.custom_name}
                            disabled={!canEditProjects}
                            onChange={(event) => updateEditProjectRow(index, { custom_name: event.target.value })}
                          />
                        </AppField>
                      ) : null}
                      {editProjects.length > 1 ? (
                        <AppField label={t("expense_report_web.create.project_time")}>
                          <AppToolbar
                            left={(
                              <ExpenseTimeInput
                                value={formatMinutesForTimeInput(project.time_min)}
                                disabled={!canEditProjects}
                                onChange={(event) => updateEditProjectRow(
                                  index,
                                  { time_min: parseTimeInputToMinutes(event.target.value) },
                                  { editedTime: true }
                                )}
                              />
                            )}
                            right={(
                              <AppTooltip content={t("expense_report_web.create.remove_project", "Projekt entfernen")}>
                                <AppButton
                                  variant="neutral"
                                  size="sm"
                                  iconOnly
                                  onClick={() => removeEditProjectRow(index)}
                                  disabled={!canEditProjects}
                                  aria-label={t("expense_report_web.create.remove_project", "Projekt entfernen")}
                                >
                                  <IconRemove />
                                </AppButton>
                              </AppTooltip>
                            )}
                          />
                        </AppField>
                      ) : null}
                    </AppFieldGrid>
                  );
                })}
                {editTravelExcess > 0 ? (
                  <AppField label={t("expense_report_web.create.project")}>
                    <AppInput
                      value={`${t("expense_report_web.table.travel_time")} — ${formatMinutesHm(editTravelExcess)}`}
                      readOnly
                      title={t("expense_report_web.create.travel_project_hint")}
                    />
                  </AppField>
                ) : null}
                <AppButton
                  variant="neutral"
                  size="sm"
                  onClick={addEditProjectRow}
                  disabled={editProjects.length >= 3 || !canEditProjects}
                >
                  <IconAdd />
                  {t("expense_report_web.create.add_project")}
                </AppButton>
              </AppFieldStack>
            </>
          ) : null}
        </AppDialogBody>
        <AppDialogFooter>
          <AppButton variant="neutral" size="md" onClick={closeEditDialog} disabled={isSaving}>
            {t("expense_report_web.actions.cancel")}
          </AppButton>
          <AppButton variant="primary" size="md" onClick={saveEditDialog} disabled={isSaving}>
            {t("expense_report_web.actions.save")}
          </AppButton>
        </AppDialogFooter>
      </AppDialog>

      <AppDialog open={createOpen} onClose={isSaving ? undefined : closeCreateDialog} size="lg">
        <AppDialogHeader title={t("expense_report_web.create.entry_title", t("expense_report_web.create.title"))} />
        <AppDialogBody>
          <>
            <AppFieldStack size="md">
              <ExpenseDialogSectionHeader
                number="1"
                title={t("expense_report_web.create.expense_data_title")}
              />
              {isAdmin ? (
                <AppField label={t("expense_report_web.table.employee")} required>
                  <AppSelect
                    searchable
                    value={createForm.email}
                    onChange={(event) => setCreateForm((prev) => ({ ...prev, email: event.target.value }))}
                    options={userOptions.map((user) => ({ value: user.email, label: user.label }))}
                  />
                </AppField>
              ) : null}

              <AppFieldGrid columns={3} size="md">
              <AvailabilityDateField
                label={t("expense_report_web.create.date")}
                value={createForm.date}
                min={isAdmin ? undefined : formatDateForInput(minSelectableExpenseDate)}
                required
                onChange={(value) => setCreateForm((prev) => ({ ...prev, date: value }))}
              />
              <AppField label={t("expense_report_web.create.start")} required>
                <ExpenseTimeInput
                  value={createForm.start_time}
                  onChange={(event) => setCreateForm((prev) => ({ ...prev, start_time: event.target.value }))}
                />
              </AppField>
              <AppField label={t("expense_report_web.create.end")} required>
                <ExpenseTimeInput
                  value={createForm.end_time}
                  onChange={(event) => setCreateForm((prev) => ({ ...prev, end_time: event.target.value }))}
                />
              </AppField>
              </AppFieldGrid>
              <AppFieldGrid columns={4} size="md">
              <AppField label={t("expense_report_web.create.pause")}>
                <AppSelect
                  value={createForm.pause_time}
                  onChange={(event) => setCreateForm((prev) => ({ ...prev, pause_time: Number(event.target.value) }))}
                  options={createPauseOptions.map((value) => ({ value, label: String(value) }))}
                />
              </AppField>
              <AppField
                label={t("expense_report_web.create.travel_time")}
                help={t("expense_report_web.create.travel_time_help")}
              >
                <AppInput
                  type="number"
                  min="0"
                  step="1"
                  value={createForm.travel_time}
                  onChange={(event) => setCreateForm((prev) => ({ ...prev, travel_time: event.target.value }))}
                />
              </AppField>
              <AppField label={t("expense_report_web.create.km")}>
                <AppInput
                  type="number"
                  min="0"
                  step="1"
                  value={createForm.km_driven}
                  onChange={(event) => setCreateForm((prev) => ({ ...prev, km_driven: event.target.value }))}
                />
              </AppField>
              <AppField label={t("expense_report_web.create.dined_out")}>
                <AppSelect
                  value={createForm.dined_out ? "yes" : "no"}
                  onChange={(event) => setCreateForm((prev) => ({ ...prev, dined_out: event.target.value === "yes" }))}
                  options={[
                    { value: "yes", label: t("expense_report_web.boolean.yes") },
                    { value: "no", label: t("expense_report_web.boolean.no") },
                  ]}
                />
              </AppField>
              </AppFieldGrid>

              <AppField
                label={t("expense_report_web.create.note", "Kommentar")}
                help={`${(createForm.note || "").length}/500`}
              >
                <AppTextarea
                  value={createForm.note || ""}
                  rows={3}
                  onChange={(event) => setCreateForm((prev) => ({ ...prev, note: event.target.value.slice(0, 500) }))}
                />
              </AppField>
            </AppFieldStack>

            <AppDivider spacing="lg" />
            <AppFieldStack size="md">
              <ExpenseDialogSectionHeader
                number="2"
                title={t("expense_report_web.create.project_allocation_title")}
                subtitle={showWorkedCreateHint
                  ? (workedCreateMinutes === null
                    ? t("expense_report_web.toast.time_error")
                    : (createTravelExcess > 0
                      ? t("expense_report_web.create.distributable_time_hint", {
                        worked: formatMinutesHm(workedCreateMinutes),
                        distributable: formatMinutesHm(distributableCreateMinutes),
                      })
                      : t("expense_report_web.create.worked_time_hint", { worked: formatMinutesHm(workedCreateMinutes) })))
                  : null}
              />
              {showCreateBaseValidation ? (
                <AppText as="p" variant="body-2" tone="secondary">
                  {t("expense_report_web.toast.validation_error")}
                </AppText>
              ) : null}
              {showCreateTimeError ? (
                <AppText as="p" variant="body-2" tone="secondary">
                  {t("expense_report_web.toast.time_error")}
                </AppText>
              ) : null}
              {createForm.projects.map((project, index) => {
                const selectedValue = project.mode === "custom" ? "__custom__" : project.project_entry_id;
                return (
                  <AppFieldGrid key={`create-project-${index}`} columns={4} size="sm">
                    <AppField
                      label={t("expense_report_web.create.project")}
                      span={createForm.projects.length > 1
                        ? (project.mode === "custom" ? 2 : 3)
                        : (project.mode === "custom" ? 2 : 4)}
                    >
                      <AppSelect
                        searchable
                        value={selectedValue}
                        options={projectSelectOptions.map((option) => ({ value: option.id, label: option.name }))}
                        disabled={!canDefineProjects}
                        onChange={(event) => {
                          const value = event.target.value;
                          updateCreateProjectRow(index, value === "__custom__"
                            ? { mode: "custom", project_entry_id: "" }
                            : { mode: "existing", project_entry_id: value, custom_name: "" });
                        }}
                      />
                    </AppField>

                    {project.mode === "custom" ? (
                      <AppField
                        label={t("expense_report_web.create.custom_name")}
                        span={createForm.projects.length > 1 ? 1 : 2}
                      >
                        <AppInput
                          value={project.custom_name}
                          disabled={!canDefineProjects}
                          onChange={(event) => updateCreateProjectRow(index, { custom_name: event.target.value })}
                        />
                      </AppField>
                    ) : null}

                    {createForm.projects.length > 1 ? (
                      <AppField label={t("expense_report_web.create.project_time")}>
                        <AppToolbar
                          left={(
                            <ExpenseTimeInput
                              value={formatMinutesForTimeInput(project.time_min)}
                              disabled={!canDefineProjects}
                              onChange={(event) => updateCreateProjectRow(
                                index,
                                { time_min: parseTimeInputToMinutes(event.target.value) },
                                { editedTime: true }
                              )}
                            />
                          )}
                          right={(
                            <AppTooltip content={t("expense_report_web.create.remove_project", "Projekt entfernen")}>
                              <AppButton
                                variant="neutral"
                                size="sm"
                                iconOnly
                                onClick={() => removeCreateProjectRow(index)}
                                disabled={!canDefineProjects}
                                aria-label={t("expense_report_web.create.remove_project", "Projekt entfernen")}
                              >
                                <IconRemove />
                              </AppButton>
                            </AppTooltip>
                          )}
                        />
                      </AppField>
                    ) : null}
                  </AppFieldGrid>
                );
              })}
              {createTravelExcess > 0 ? (
                <AppField label={t("expense_report_web.create.project")}>
                  <AppInput
                    value={`${t("expense_report_web.table.travel_time")} — ${formatMinutesHm(createTravelExcess)}`}
                    readOnly
                    title={t("expense_report_web.create.travel_project_hint")}
                  />
                </AppField>
              ) : null}
              <AppButton
                variant="neutral"
                size="sm"
                onClick={addCreateProjectRow}
                disabled={createForm.projects.length >= 3 || !canDefineProjects}
              >
                <IconAdd />
                {t("expense_report_web.create.add_project")}
              </AppButton>
            </AppFieldStack>
          </>
        </AppDialogBody>
        <AppDialogFooter>
          <AppButton variant="neutral" size="md" onClick={closeCreateDialog} disabled={isSaving}>
            {t("expense_report_web.actions.cancel")}
          </AppButton>
          <AppButton variant="primary" size="md" onClick={saveCreateDialog} disabled={isSaving}>
            {t("expense_report_web.actions.entry_create", t("expense_report_web.actions.create"))}
          </AppButton>
        </AppDialogFooter>
      </AppDialog>

      <DownloadBuilder
        open={exportOpen}
        onClose={closeExportDialog}
        title="Download erstellen"
        documentTitle="Spesenbericht"
        description={`${exportPreview?.period_label || "Aktueller Zeitraum"} · ${exportPreview?.scope_label || "Sichtbare Mitarbeitende"}`}
        columns={exportPreview?.columns || []}
        defaultColumnIds={exportPreview?.default_column_ids || []}
        tabModes={exportPreview?.tab_modes || []}
        defaultTabMode={exportPreview?.default_tab_mode || "none"}
        timeframeOptions={[
          { id: "month", label: "Monat" },
          { id: "range", label: "Benutzerdefiniert" },
        ]}
        defaultTimeframe={exportTimeframe}
        onTimeframeChange={handleExportTimeframeChange}
        filterGroups={exportPreview?.filter_groups || []}
        defaultFilters={exportPreview?.default_filters || exportFilters}
        resetKey={exportResetKey}
        onFiltersChange={handleExportFiltersChange}
        onTabModeChange={(tabMode) => {
          setExportTabMode(tabMode);
          loadExportPreview(exportFilters, exportTimeframe, tabMode).catch(() => {});
        }}
        previewRows={exportPreview?.preview_rows || []}
        previewSections={exportPreview?.preview_sections || []}
        previewLoading={isExportPreviewLoading}
        isDownloading={isExporting}
        onDownload={handleExpenseDownload}
        dateFieldComponent={AvailabilityDateField}
        pdfEnabled
      />

      <AppConfirmDialog
        open={deleteOpen}
        size="sm"
        title={t("expense_report_web.delete.bulk_title")}
        description={t("expense_report_web.delete.bulk_question", {
          count: selectedEntryIds.length,
        })}
        cancelLabel={t("expense_report_web.actions.cancel")}
        confirmLabel={t("expense_report_web.delete.confirm")}
        confirmTone="error"
        confirmIcon={<IconDelete />}
        onClose={closeDeleteDialog}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default ExpenseReportWeb;
